import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  CheckCircle2,
  Building2,
  Users,
  User,
  Calendar,
  CreditCard,
  Globe,
  MessageSquare,
  Scissors,
  Palette,
  Leaf,
  Eye,
  PartyPopper,
  GraduationCap,
  Instagram,
  Smartphone,
  Mail,
  MapPin,
  Clock,
  Star,
  Zap,
  Settings,
  Layers,
  BarChart3,
  Heart,
  Camera,
  Link2,
  Send,
  Gift,
  Shield,
  Loader2,
  Search,
  Repeat,
  UserPlus,
  Handshake,
  Copy,
  ClipboardList,
  Edit2,
  Upload,
  Image
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// New flow sections - Walk through your experience
const JOURNEY_SECTIONS = [
  { id: "discovery", title: "Discovery", icon: Search, desc: "How clients find you" },
  { id: "booking", title: "First Booking", icon: Calendar, desc: "How they book with you" },
  { id: "service", title: "Service Day", icon: Sparkles, desc: "What happens during" },
  { id: "returning", title: "Returning", icon: Repeat, desc: "Building relationships" },
  { id: "setup", title: "Your Setup", icon: Settings, desc: "Configure your portal" }
];

// Discovery channels
const DISCOVERY_CHANNELS = [
  { id: "instagram", name: "Instagram", icon: Instagram, placeholder: "@yourhandle" },
  { id: "tiktok", name: "TikTok", icon: Camera, placeholder: "@yourhandle" },
  { id: "facebook", name: "Facebook", icon: Users, placeholder: "facebook.com/..." },
  { id: "website", name: "Website", icon: Globe, placeholder: "yourwebsite.com" },
  { id: "google", name: "Google Business", icon: MapPin, placeholder: "Business name" },
  { id: "referral", name: "Word of Mouth", icon: MessageSquare, placeholder: "" },
  { id: "yelp", name: "Yelp", icon: Star, placeholder: "Business name" },
  { id: "styleseat", name: "StyleSeat", icon: Scissors, placeholder: "Profile link" },
  { id: "booksy", name: "Booksy", icon: Calendar, placeholder: "Profile link" },
  { id: "other", name: "Other", icon: Link2, placeholder: "Describe..." }
];

// Booking methods
const BOOKING_METHODS = [
  { id: "dm", name: "DM / Text", icon: MessageSquare, desc: "Clients message you directly" },
  { id: "phone", name: "Phone Call", icon: Smartphone, desc: "Old school, still works" },
  { id: "booking_platform", name: "Booking Platform", icon: Calendar, desc: "Acuity, Calendly, etc." },
  { id: "walkin", name: "Walk-ins", icon: UserPlus, desc: "No appointment needed" },
  { id: "other", name: "Other", icon: Settings, desc: "Something else" }
];

const BOOKING_PLATFORMS = [
  "Acuity", "Calendly", "Square", "Vagaro", "Fresha", "StyleSeat", "Booksy", "Mindbody", "Jane App", "Other"
];

// Payment methods
const PAYMENT_METHODS = [
  { id: "venmo", name: "Venmo" },
  { id: "cashapp", name: "Cash App" },
  { id: "zelle", name: "Zelle" },
  { id: "square", name: "Square" },
  { id: "stripe", name: "Stripe" },
  { id: "paypal", name: "PayPal" },
  { id: "cash", name: "Cash" },
  { id: "card", name: "Card Reader" },
  { id: "other", name: "Other" }
];

// Admin settings (for Calendly link toggle etc)
const DEFAULT_SETTINGS = {
  calendly_enabled: true,
  calendly_url: "https://calendly.com/phygital-team"
};

// Multi-select checkbox component for proper multi-select UI
function MultiSelectCheckboxGroup({ options, selected, onChange, columns = 2, showIcons = false }) {
  const selectedArray = selected || [];
  
  const toggleOption = (optionId) => {
    const updated = selectedArray.includes(optionId)
      ? selectedArray.filter(id => id !== optionId)
      : [...selectedArray, optionId];
    onChange(updated);
  };
  
  return (
    <div className={`grid gap-3 ${columns === 1 ? "grid-cols-1" : columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
      {options.map((option) => {
        const isSelected = selectedArray.includes(option.id || option);
        const optionId = option.id || option;
        const optionLabel = option.name || option.label || option;
        const Icon = option.icon;
        
        return (
          <label 
            key={optionId}
            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
              isSelected 
                ? "border-purple-500 bg-purple-50" 
                : "border-gray-200 hover:border-purple-200 bg-white"
            }`}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleOption(optionId)}
              className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
            />
            {showIcons && Icon && <Icon className={`w-4 h-4 ${isSelected ? "text-purple-600" : "text-gray-400"}`} />}
            <span className={`text-sm ${isSelected ? "text-purple-700 font-medium" : "text-gray-700"}`}>
              {optionLabel}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export default function VisualIntakeBuilder() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Journey data - prefilled where possible
  const [journeyData, setJourneyData] = useState({
    // Pre-filled from invite (editable)
    business_name: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location: "",
    
    // Discovery - where clients find them
    discovery_channels: {},  // { instagram: "@handle", website: "url", etc. }
    primary_discovery: "",
    
    // First Booking
    booking_method: "",
    booking_platform: "",
    typical_response_time: "",
    
    // Service Day
    service_location: [], // home, studio, mobile - now multi-select
    appointment_prep: [],  // what clients need to do before
    appointment_prep_other: "",
    what_to_bring: "",
    
    // Returning Clients
    follow_up_method: "",
    rebooking_approach: "",
    client_retention_notes: "",
    
    // Setup / Toggles
    payment_methods: [],
    show_prices: true,
    require_deposit: false,
    send_reminders: true,
    collect_preferences: true,
    
    // "Other" text inputs
    discovery_other: "",
    booking_method_other: "",
    booking_platform_other: "",
    payment_other: "",
    
    // Additional fields
    anything_else: "",  // "Anything else to share to make your experience, yours?"
    // schedule_call field removed per user request
    
    // NEW: Common Questions & Communication
    common_questions: "",  // Questions they repeat to every client
    info_wish_clients_knew: "",  // Info they wish clients knew beforehand
    
    // NEW: Products & Brands
    brands_used: "",  // List all brands/products they use
    sell_products: false,
    products_sold: "",  // Which products they sell
    product_recommendations: "",  // How they handle recommendations
    
    // NEW: Pre-Appointment Prep (Provider Side)
    provider_prep_routine: "",  // How they prepare before clients
    supplies_setup: "",  // Materials they set up
    review_client_history: false,
    
    // NEW: Business Operations
    cancellation_policy: "",
    pricing_discussion: "",  // How they handle pricing discussions
    offers_packages: false,
    packages_details: "",
    
    // NEW: Client Defaults (what they need to know about each client)
    client_defaults: [],  // Custom fields they want to collect
    client_defaults_other: "",
    
    // NEW: Client Inspiration
    inspo_collection_method: "",  // How clients share inspo
    inspo_platforms: [],  // Where they want clients to upload
    uses_mood_boards: false,
    
    // Logo
    has_logo: false,
    no_logo: false,
    logo_url: ""
  });
  
  const [showReview, setShowReview] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);

  useEffect(() => {
    const fetchPortal = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/pilot/portal/${portalId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (!res.ok) throw new Error("Portal not found");
        const data = await res.json();
        setPortal(data.portal);
        
        // Prefill from portal/user data - prioritize user data from the response
        const p = data.portal || {};
        const u = data.user || {};
        const prefill = p.prefill || {};  // Admin pre-filled data
        
        setJourneyData(prev => ({
          ...prev,
          // User info - prioritize user object, then portal object
          first_name: u.first_name || p.first_name || prev.first_name,
          last_name: u.last_name || p.last_name || prev.last_name,
          email: u.email || p.email || p.user_email || prev.email,
          phone: u.phone || p.phone || prev.phone,
          // Business info
          business_name: u.business_name || p.company_name || p.business_name || prev.business_name,
          location: prefill.location || u.city || u.location || p.city || p.location || prev.location,
          // Pre-fill from admin data
          discovery_channels: {
            ...(prev.discovery_channels || {}),
            instagram: prefill.instagram || prev.discovery_channels?.instagram || "",
            tiktok: prefill.tiktok || prev.discovery_channels?.tiktok || "",
            facebook: prefill.facebook || prev.discovery_channels?.facebook || "",
            website: prefill.website || prev.discovery_channels?.website || "",
            google: prefill.google_business || prev.discovery_channels?.google || "",
            yelp: prefill.yelp || prev.discovery_channels?.yelp || "",
            styleseat: prefill.styleseat || prev.discovery_channels?.styleseat || "",
            booksy: prefill.booksy || prev.discovery_channels?.booksy || ""
          },
          booking_platforms: prefill.booking_platforms?.length > 0 
            ? prefill.booking_platforms 
            : prev.booking_platforms,
          // Normalize payment methods to lowercase IDs (admin uses display names like "Venmo", form uses "venmo")
          payment_methods: prefill.payment_methods?.length > 0 
            ? prefill.payment_methods.map(m => m.toLowerCase().replace(/\s+/g, ''))
            : prev.payment_methods,
          services_offered: prefill.services_offered || prev.services_offered,
          years_experience: prefill.years_experience || prev.years_experience,
          // Load existing intake responses (overrides above if already saved)
          ...(p.intake_responses || {}),
          // Re-normalize payment_methods from intake_responses if present
          ...(p.intake_responses?.payment_methods?.length > 0 ? {
            payment_methods: p.intake_responses.payment_methods.map(m => 
              typeof m === 'string' ? m.toLowerCase().replace(/\s+/g, '') : m
            )
          } : {})
        }));
        
        console.log("Prefill data:", { portal: p, user: u, prefill });
      } catch (err) {
        toast.error("Failed to load portal");
        console.error("Portal fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPortal();
  }, [portalId]);

  const updateField = (field, value) => {
    setJourneyData(prev => ({ ...prev, [field]: value }));
  };

  const updateDiscoveryChannel = (channelId, value) => {
    setJourneyData(prev => ({
      ...prev,
      discovery_channels: {
        ...prev.discovery_channels,
        [channelId]: value
      }
    }));
  };

  const toggleArrayField = (field, value) => {
    setJourneyData(prev => {
      const current = prev[field] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const handleSave = async (isComplete = false) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/intake`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          intake_responses: journeyData,
          intake_stage: currentSection + 1,
          intake_completed: isComplete
        })
      });
      
      if (res.ok) {
        if (!isComplete) {
          toast.success("Progress saved!");
        }
        return true;
      }
      return false;
    } catch (err) {
      toast.error("Failed to save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await handleSave(false);
    if (currentSection < JOURNEY_SECTIONS.length - 1) {
      setCurrentSection(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      // Complete intake
      const saved = await handleSave(true);
      if (saved) {
        toast.success("You're all set! Redirecting to your confirmation page...");
        setTimeout(() => navigate(`/pilot/confirmation/${portalId}`), 1500);
      }
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const progress = ((currentSection + 1) / JOURNEY_SECTIONS.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <p className="text-gray-500">Loading your experience...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Sparkles className="w-6 h-6 text-purple-600" />
            <span className="font-semibold text-lg">phygital</span>
          </a>
          <Badge variant="secondary" className="bg-purple-100 text-purple-700">
            Walk Me Through Your Experience
          </Badge>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              {JOURNEY_SECTIONS[currentSection].title}: {JOURNEY_SECTIONS[currentSection].desc}
            </span>
            <span className="text-sm text-gray-400">
              {currentSection + 1} of {JOURNEY_SECTIONS.length}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Section Pills */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {JOURNEY_SECTIONS.map((section, idx) => {
              const Icon = section.icon;
              const isComplete = idx < currentSection;
              const isCurrent = idx === currentSection;
              return (
                <button
                  key={section.id}
                  onClick={() => idx <= currentSection && setCurrentSection(idx)}
                  disabled={idx > currentSection}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                    isCurrent
                      ? "bg-purple-600 text-white"
                      : isComplete
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {isComplete ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                  {section.title}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Section 1: Discovery */}
        {currentSection === 0 && (
          <div className="space-y-8" data-testid="intake-section-discovery">
            <div className="text-center mb-8">
              <Search className="w-12 h-12 mx-auto mb-4 text-purple-600" />
              <h2 className="text-2xl font-bold mb-2">How do clients find you?</h2>
              <p className="text-gray-600">Link all the places where clients discover you</p>
            </div>

            {/* Pre-filled Business Info (editable) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Info</CardTitle>
                <CardDescription>Pre-filled from your invite, edit if needed</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Business Name</Label>
                  <Input
                    value={journeyData.business_name}
                    onChange={(e) => updateField("business_name", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Location (City)</Label>
                  <Input
                    value={journeyData.location}
                    onChange={(e) => updateField("location", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={journeyData.first_name}
                    onChange={(e) => updateField("first_name", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={journeyData.last_name}
                    onChange={(e) => updateField("last_name", e.target.value)}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Discovery Channels with Links */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Where Clients Find You</CardTitle>
                <CardDescription>Add your links so we can connect them</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {DISCOVERY_CHANNELS.map((channel) => {
                  const Icon = channel.icon;
                  const hasValue = journeyData.discovery_channels?.[channel.id];
                  return (
                    <div key={channel.id} className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        hasValue ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-500"
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm">{channel.name}</Label>
                        {channel.placeholder ? (
                          <Input
                            value={journeyData.discovery_channels?.[channel.id] || ""}
                            onChange={(e) => updateDiscoveryChannel(channel.id, e.target.value)}
                            placeholder={channel.placeholder}
                            className="mt-1"
                          />
                        ) : (
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="checkbox"
                              checked={!!journeyData.discovery_channels?.[channel.id]}
                              onChange={(e) => updateDiscoveryChannel(channel.id, e.target.checked ? "yes" : "")}
                              className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-500">Clients find me through referrals</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Primary Discovery - Multi-select with Checkboxes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Where do MOST clients find you?</CardTitle>
                <CardDescription>Select all that apply (pick your top sources)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <MultiSelectCheckboxGroup
                  options={DISCOVERY_CHANNELS}
                  selected={journeyData.primary_discovery_channels || []}
                  onChange={(updated) => updateField("primary_discovery_channels", updated)}
                  columns={3}
                  showIcons={true}
                />
                
                {/* Show "Other" input if selected */}
                {(journeyData.primary_discovery_channels || []).includes("other") && (
                  <div className="mt-4">
                    <Label>Tell us about your other discovery channel</Label>
                    <Input
                      value={journeyData.discovery_other || ""}
                      onChange={(e) => updateField("discovery_other", e.target.value)}
                      placeholder="e.g., Local events, community boards, salon referrals..."
                      className="mt-1"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Section 2: First Booking */}
        {currentSection === 1 && (
          <div className="space-y-8" data-testid="intake-section-booking">
            <div className="text-center mb-8">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-purple-600" />
              <h2 className="text-2xl font-bold mb-2">How do clients book with you?</h2>
              <p className="text-gray-600">Walk us through the first-time booking experience</p>
            </div>

            {/* Booking Method */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Primary Booking Method</CardTitle>
                <CardDescription>How do new clients typically reach out?</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {BOOKING_METHODS.map((method) => {
                    const Icon = method.icon;
                    const selected = journeyData.booking_method === method.id;
                    return (
                      <button
                        key={method.id}
                        onClick={() => updateField("booking_method", method.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selected
                            ? "bg-purple-50 border-purple-500"
                            : "bg-white border-gray-200 hover:border-purple-300"
                        }`}
                      >
                        <Icon className={`w-6 h-6 mb-2 ${selected ? "text-purple-600" : "text-gray-400"}`} />
                        <p className="font-medium">{method.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{method.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* If "Other" booking method */}
            {journeyData.booking_method === "other" && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tell us about your booking process</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    value={journeyData.booking_method_other || ""}
                    onChange={(e) => updateField("booking_method_other", e.target.value)}
                    placeholder="Describe how clients book with you..."
                    className="w-full"
                  />
                </CardContent>
              </Card>
            )}

            {/* If booking platform */}
            {journeyData.booking_method === "booking_platform" && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Which platform?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {BOOKING_PLATFORMS.map((platform) => (
                      <button
                        key={platform}
                        onClick={() => updateField("booking_platform", platform)}
                        className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${
                          journeyData.booking_platform === platform
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white border-gray-200 hover:border-purple-300"
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                  
                  {/* Show "Other" input if selected */}
                  {journeyData.booking_platform === "Other" && (
                    <div className="mt-4">
                      <Label>Which booking platform do you use?</Label>
                      <Input
                        value={journeyData.booking_platform_other || ""}
                        onChange={(e) => updateField("booking_platform_other", e.target.value)}
                        placeholder="Enter your booking platform name..."
                        className="mt-1"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Response Time */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How quickly do you respond?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {["Within an hour", "Same day", "Within 24 hours", "When I can"].map((time) => (
                    <button
                      key={time}
                      onClick={() => updateField("typical_response_time", time)}
                      className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${
                        journeyData.typical_response_time === time
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Section 3: Service Day */}
        {currentSection === 2 && (
          <div className="space-y-8" data-testid="intake-section-service">
            <div className="text-center mb-8">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-600" />
              <h2 className="text-2xl font-bold mb-2">Service Day</h2>
              <p className="text-gray-600">What happens when clients come to you?</p>
            </div>

            {/* Service Location - Multi-select with Checkboxes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Where do you provide services?</CardTitle>
                <CardDescription>Select all that apply</CardDescription>
              </CardHeader>
              <CardContent>
                <MultiSelectCheckboxGroup
                  options={[
                    { id: "home", name: "My home/studio" },
                    { id: "salon", name: "Salon/Spa" },
                    { id: "mobile", name: "I travel to clients" },
                    { id: "rented_space", name: "Rented space/booth" },
                    { id: "outdoor", name: "Outdoor/Events" }
                  ]}
                  selected={journeyData.service_location || []}
                  onChange={(updated) => updateField("service_location", updated)}
                  columns={2}
                />
              </CardContent>
            </Card>

            {/* Appointment Prep - Multi-select with Checkboxes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What should clients do before arriving?</CardTitle>
                <CardDescription>Select all that apply</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <MultiSelectCheckboxGroup
                  options={[
                    "Wash hair/nails",
                    "No product in hair",
                    "Bring reference photos",
                    "Eat beforehand",
                    "Arrive with clean face",
                    "Bring their own supplies",
                    "Nothing specific",
                    "Other"
                  ]}
                  selected={journeyData.appointment_prep || []}
                  onChange={(updated) => updateField("appointment_prep", updated)}
                  columns={2}
                />
                
                {/* Show "Other" input if selected */}
                {(journeyData.appointment_prep || []).includes("Other") && (
                  <div className="mt-4">
                    <Label>What else should clients do before arriving?</Label>
                    <Input
                      value={journeyData.appointment_prep_other || ""}
                      onChange={(e) => updateField("appointment_prep_other", e.target.value)}
                      placeholder="Describe other preparation needed..."
                      className="mt-1"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* What to Bring */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Anything clients need to bring?</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  value={journeyData.what_to_bring || ""}
                  onChange={(e) => updateField("what_to_bring", e.target.value)}
                  placeholder="e.g., Hair: 7-9 packs of braiding hair. Nails: Nothing, I provide everything."
                  rows={3}
                  className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Section 4: Returning Clients */}
        {currentSection === 3 && (
          <div className="space-y-8" data-testid="intake-section-returning">
            <div className="text-center mb-8">
              <Repeat className="w-12 h-12 mx-auto mb-4 text-purple-600" />
              <h2 className="text-2xl font-bold mb-2">Building Relationships</h2>
              <p className="text-gray-600">How do you keep clients coming back?</p>
            </div>

            {/* Follow-up Method */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do you follow up after appointments?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Text/DM check-in",
                    "Send photos of their look",
                    "Rebook reminder",
                    "Birthday messages",
                    "I don't follow up",
                    "Other"
                  ].map((method) => (
                    <button
                      key={method}
                      onClick={() => updateField("follow_up_method", method)}
                      className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${
                        journeyData.follow_up_method === method
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Rebooking */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do clients typically rebook?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {[
                    "They reach out when ready",
                    "I remind them",
                    "Book next appointment before leaving",
                    "Subscription/regular schedule"
                  ].map((approach) => (
                    <button
                      key={approach}
                      onClick={() => updateField("rebooking_approach", approach)}
                      className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${
                        journeyData.rebooking_approach === approach
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      {approach}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Anything else about how you build client relationships?</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  value={journeyData.client_retention_notes || ""}
                  onChange={(e) => updateField("client_retention_notes", e.target.value)}
                  placeholder="What makes clients come back to you? What would you like to do better?"
                  rows={3}
                  className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </CardContent>
            </Card>

            {/* Common Questions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What questions do you find yourself repeating to every client?</CardTitle>
                <CardDescription>These could become automated messages or intake questions</CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  value={journeyData.common_questions || ""}
                  onChange={(e) => updateField("common_questions", e.target.value)}
                  placeholder="e.g., What's your hair texture? Do you have any allergies? What look are you going for today?"
                  rows={4}
                  className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </CardContent>
            </Card>

            {/* Client Inspiration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do clients typically share their inspo with you?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {[
                    "Screenshots in DM",
                    "Pinterest boards",
                    "Show me on their phone",
                    "Describe verbally",
                    "Email photos",
                    "They don't share inspo"
                  ].map((method) => (
                    <button
                      key={method}
                      onClick={() => updateField("inspo_collection_method", method)}
                      className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${
                        journeyData.inspo_collection_method === method
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Section 5: Setup / Toggles */}
        {currentSection === 4 && (
          <div className="space-y-8" data-testid="intake-section-setup">
            <div className="text-center mb-8">
              <Settings className="w-12 h-12 mx-auto mb-4 text-purple-600" />
              <h2 className="text-2xl font-bold mb-2">Your Business & Preferences</h2>
              <p className="text-gray-600">Tell us everything so we can personalize your portal</p>
            </div>

            {/* Logo Upload */}
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Image className="w-5 h-5 text-purple-500" />
                  Your Logo
                </CardTitle>
                <CardDescription>
                  Upload your business logo to personalize your portal and client-facing pages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {journeyData.logo_url ? (
                  <div className="flex items-center gap-4">
                    <img 
                      src={journeyData.logo_url} 
                      alt="Business logo" 
                      className="w-20 h-20 object-contain border rounded-lg bg-gray-50"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-green-600 font-medium">Logo uploaded!</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => updateField("logo_url", "")}
                      >
                        Remove & Upload New
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-purple-300 transition-colors">
                    <Upload className="w-10 h-10 mx-auto mb-4 text-gray-400" />
                    <p className="text-sm text-gray-500 mb-4">
                      Drag and drop your logo, or click to browse
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="logo-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append("file", file);
                            formData.append("folder", "logos");
                            const res = await fetch(`${API}/api/pilot/upload-image`, {
                              method: "POST",
                              body: formData
                            });
                            if (res.ok) {
                              const data = await res.json();
                              updateField("logo_url", data.url);
                              toast.success("Logo uploaded!");
                            } else {
                              toast.error("Upload failed");
                            }
                          } catch (err) {
                            toast.error("Upload error");
                          }
                        }
                      }}
                    />
                    <label 
                      htmlFor="logo-upload"
                      className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Choose File
                    </label>
                    <p className="text-xs text-gray-400 mt-3">
                      PNG, JPG, or SVG • Max 2MB • Square works best
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Defaults - What they need to know */}
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-500" />
                  What do you need to know about every client?
                </CardTitle>
                <CardDescription>
                  Select all the information you always collect (we'll help you gather this automatically)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <MultiSelectCheckboxGroup
                  options={[
                    "Allergies",
                    "Skin type",
                    "Hair color",
                    "Hair texture",
                    "Sensitivities",
                    "Dietary restrictions",
                    "Preferred scents",
                    "Medical conditions",
                    "Previous treatments",
                    "Style preferences",
                    "Other"
                  ]}
                  selected={journeyData.client_defaults || []}
                  onChange={(updated) => updateField("client_defaults", updated)}
                  columns={2}
                />
                
                {(journeyData.client_defaults || []).includes("Other") && (
                  <div className="mt-4">
                    <Label>What other information do you collect?</Label>
                    <Input
                      value={journeyData.client_defaults_other || ""}
                      onChange={(e) => updateField("client_defaults_other", e.target.value)}
                      placeholder="e.g., nail shape preference, favorite colors, etc."
                      className="mt-1"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Products & Brands */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Products & Brands</CardTitle>
                <CardDescription>Tell us what you use and sell</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>List the brands/products you use in your services</Label>
                  <textarea
                    value={journeyData.brands_used || ""}
                    onChange={(e) => updateField("brands_used", e.target.value)}
                    placeholder="e.g., Olaplex, Redken, OPI, CND, MAC, etc."
                    rows={3}
                    className="w-full mt-1 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Do you sell products to clients?</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={journeyData.sell_products === true ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateField("sell_products", true)}
                      className={journeyData.sell_products === true ? "bg-purple-600" : ""}
                    >
                      Yes
                    </Button>
                    <Button
                      variant={journeyData.sell_products === false ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateField("sell_products", false)}
                      className={journeyData.sell_products === false ? "bg-gray-600" : ""}
                    >
                      No
                    </Button>
                  </div>
                </div>
                
                {journeyData.sell_products === true && (
                  <div>
                    <Label>Which products do you sell?</Label>
                    <textarea
                      value={journeyData.products_sold || ""}
                      onChange={(e) => updateField("products_sold", e.target.value)}
                      placeholder="List the products you sell to clients..."
                      rows={2}
                      className="w-full mt-1 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Provider Pre-Appointment Prep */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do you prepare before a client arrives?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Your prep routine</Label>
                  <textarea
                    value={journeyData.provider_prep_routine || ""}
                    onChange={(e) => updateField("provider_prep_routine", e.target.value)}
                    placeholder="e.g., Review their preference card, set up color station, prepare tools..."
                    rows={3}
                    className="w-full mt-1 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Do you review client history before appointments?</p>
                    <p className="text-sm text-gray-500">Looking at past notes, photos, preferences</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={journeyData.review_client_history === true ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateField("review_client_history", true)}
                      className={journeyData.review_client_history === true ? "bg-purple-600" : ""}
                    >
                      Yes
                    </Button>
                    <Button
                      variant={journeyData.review_client_history === false ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateField("review_client_history", false)}
                      className={journeyData.review_client_history === false ? "bg-gray-600" : ""}
                    >
                      No
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Operations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Business Operations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>What's your cancellation/no-show policy?</Label>
                  <textarea
                    value={journeyData.cancellation_policy || ""}
                    onChange={(e) => updateField("cancellation_policy", e.target.value)}
                    placeholder="e.g., 24-hour cancellation notice, 50% deposit forfeit..."
                    rows={2}
                    className="w-full mt-1 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Do you offer packages or memberships?</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={journeyData.offers_packages === true ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateField("offers_packages", true)}
                      className={journeyData.offers_packages === true ? "bg-purple-600" : ""}
                    >
                      Yes
                    </Button>
                    <Button
                      variant={journeyData.offers_packages === false ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateField("offers_packages", false)}
                      className={journeyData.offers_packages === false ? "bg-gray-600" : ""}
                    >
                      No
                    </Button>
                  </div>
                </div>
                
                {journeyData.offers_packages === true && (
                  <div>
                    <Label>Describe your packages/memberships</Label>
                    <textarea
                      value={journeyData.packages_details || ""}
                      onChange={(e) => updateField("packages_details", e.target.value)}
                      placeholder="e.g., Monthly touch-up package, VIP membership with priority booking..."
                      rows={2}
                      className="w-full mt-1 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment Methods You Accept</CardTitle>
                <CardDescription>Select all that apply</CardDescription>
              </CardHeader>
              <CardContent>
                <MultiSelectCheckboxGroup
                  options={PAYMENT_METHODS}
                  selected={journeyData.payment_methods || []}
                  onChange={(updated) => updateField("payment_methods", updated)}
                  columns={3}
                />
              </CardContent>
            </Card>

            {/* Toggles - Show only relevant ones based on whether using Phygital for booking */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {journeyData.booking_method === "booking_platform" 
                    ? "Preference Collection Settings" 
                    : "Portal Preferences"
                  }
                </CardTitle>
                <CardDescription>
                  {journeyData.booking_method === "booking_platform"
                    ? "Configure how you collect client preferences"
                    : "Turn features on or off"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Always show preference collection toggle */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Collect client preferences</p>
                    <p className="text-xs text-gray-500">Capture style, vibe, allergies, etc.</p>
                  </div>
                  <button
                    onClick={() => updateField("collect_preferences", !journeyData.collect_preferences)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      journeyData.collect_preferences !== false ? "bg-purple-600" : "bg-gray-200"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      journeyData.collect_preferences !== false ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                  </button>
                </div>

                {/* Always show reminders */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Send appointment reminders</p>
                    <p className="text-xs text-gray-500">Automatic day-before reminders</p>
                  </div>
                  <button
                    onClick={() => updateField("send_reminders", !journeyData.send_reminders)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      journeyData.send_reminders !== false ? "bg-purple-600" : "bg-gray-200"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      journeyData.send_reminders !== false ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                  </button>
                </div>

                {/* Only show these if NOT using external booking platform */}
                {journeyData.booking_method !== "booking_platform" && (
                  <>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Show prices to clients</p>
                        <p className="text-xs text-gray-500">Display your pricing on your menu</p>
                      </div>
                      <button
                        onClick={() => updateField("show_prices", !journeyData.show_prices)}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          journeyData.show_prices ? "bg-purple-600" : "bg-gray-200"
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          journeyData.show_prices ? "translate-x-6" : "translate-x-0.5"
                        }`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Require deposits</p>
                        <p className="text-xs text-gray-500">Ask for payment upfront</p>
                      </div>
                      <button
                        onClick={() => updateField("require_deposit", !journeyData.require_deposit)}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          journeyData.require_deposit ? "bg-purple-600" : "bg-gray-200"
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          journeyData.require_deposit ? "translate-x-6" : "translate-x-0.5"
                        }`} />
                      </button>
                    </div>
                  </>
                )}

                {/* Note for booking platform users */}
                {journeyData.booking_method === "booking_platform" && (
                  <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
                    <p className="font-medium">Using {journeyData.booking_platform || "external booking"}</p>
                    <p className="text-xs mt-1">Pricing and deposits will be managed through your booking platform. Phygital will focus on collecting and organizing client preferences for you.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* "Other" Payment Method Input */}
            {(journeyData.payment_methods || []).includes("other") && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tell us about your other payment method</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    value={journeyData.payment_other || ""}
                    onChange={(e) => updateField("payment_other", e.target.value)}
                    placeholder="Describe your payment method..."
                    className="w-full"
                  />
                </CardContent>
              </Card>
            )}

            {/* Anything Else to Share */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Anything else to share to make your experience, yours?
                </CardTitle>
                <CardDescription>
                  Tell us about your unique style, special requests, or anything that makes your business special
                </CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  value={journeyData.anything_else || ""}
                  onChange={(e) => updateField("anything_else", e.target.value)}
                  placeholder="e.g., I specialize in natural textures... My clients love the home studio vibe... I always play 90s R&B during appointments..."
                  rows={4}
                  className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </CardContent>
            </Card>

            {/* Final Message */}
            <Card className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <CardContent className="p-6 text-center">
                <Gift className="w-12 h-12 mx-auto mb-4 opacity-80" />
                <h3 className="text-xl font-bold mb-2">You're Almost There!</h3>
                <p className="opacity-90 mb-4">
                  Once you submit, the Phygital team will use your responses to build your personalized portal.
                  We'll notify you when it's ready!
                </p>
                <p className="text-sm opacity-75">
                  As a pilot partner, you'll have lifetime access to Phygital Legacy tier.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentSection === 0}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            
            {/* Save & Come Back Later */}
            <Button
              variant="ghost"
              onClick={async () => {
                await handleSave(false);
                const link = `${window.location.origin}/pilot/intake/${portalId}`;
                navigator.clipboard.writeText(link);
                toast.success("Progress saved! Link copied to clipboard - you can return anytime.");
              }}
              className="text-gray-600"
            >
              <Copy className="w-4 h-4 mr-2" />
              Save & Copy Link
            </Button>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {currentSection === JOURNEY_SECTIONS.length - 1 ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowReview(true)}
                  className="flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Review Answers
                </Button>
                <Button
                  onClick={() => setShowSummaryDialog(true)}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
                  data-testid="intake-submit-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Submit & Finish
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button
                onClick={handleNext}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
                data-testid="intake-next-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </main>
      
      {/* Review Popup */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Review Your Responses
            </DialogTitle>
            <DialogDescription>
              Check your answers before submitting. Click any section to edit.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Discovery Section */}
            <ReviewSection 
              title="Discovery"
              icon={Globe}
              onEdit={() => { setShowReview(false); setCurrentSection(0); }}
              items={[
                { label: "Where clients find you", value: formatArrayOrString(journeyData.primary_discovery_channels) },
                { label: "Active channels", value: formatDiscoveryChannels(journeyData.discovery_channels) }
              ]}
            />
            
            {/* First Booking */}
            <ReviewSection 
              title="First Booking"
              icon={Calendar}
              onEdit={() => { setShowReview(false); setCurrentSection(1); }}
              items={[
                { label: "Booking method", value: journeyData.booking_method },
                { label: "Platform", value: journeyData.booking_platform },
                { label: "Response time", value: journeyData.typical_response_time }
              ]}
            />
            
            {/* Service Day */}
            <ReviewSection 
              title="Service Day"
              icon={Sparkles}
              onEdit={() => { setShowReview(false); setCurrentSection(2); }}
              items={[
                { label: "Service location", value: formatArrayOrString(journeyData.service_location) },
                { label: "Client prep", value: formatArrayOrString(journeyData.appointment_prep) },
                { label: "What to bring", value: journeyData.what_to_bring }
              ]}
            />
            
            {/* Returning Clients */}
            <ReviewSection 
              title="Building Relationships"
              icon={Heart}
              onEdit={() => { setShowReview(false); setCurrentSection(3); }}
              items={[
                { label: "Follow-up method", value: journeyData.follow_up_method },
                { label: "Rebooking approach", value: journeyData.rebooking_approach },
                { label: "Common questions", value: journeyData.common_questions?.substring(0, 100) + (journeyData.common_questions?.length > 100 ? "..." : "") }
              ]}
            />
            
            {/* Setup */}
            <ReviewSection 
              title="Your Setup"
              icon={Settings}
              onEdit={() => { setShowReview(false); setCurrentSection(4); }}
              items={[
                { label: "Client defaults collected", value: formatArrayOrString(journeyData.client_defaults) },
                { label: "Brands used", value: journeyData.brands_used?.substring(0, 100) + (journeyData.brands_used?.length > 100 ? "..." : "") },
                { label: "Payment methods", value: formatArrayOrString(journeyData.payment_methods) },
                { label: "Has logo", value: journeyData.has_logo ? "Yes" : journeyData.no_logo ? "No logo yet" : "Not specified" }
              ]}
            />
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowReview(false)}>
              Continue Editing
            </Button>
            <Button 
              onClick={() => { setShowReview(false); handleNext(); }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirm & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Summary Dialog Before Final Submit */}
      <Dialog open={showSummaryDialog} onOpenChange={setShowSummaryDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Review & Confirm Your Submission
            </DialogTitle>
            <DialogDescription>
              Please review your responses below. You can edit them here or go back to make changes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Personal Info */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-purple-600" />
                Your Info
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">First Name</Label>
                  <Input
                    value={journeyData.first_name || ""}
                    onChange={(e) => updateField("first_name", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Last Name</Label>
                  <Input
                    value={journeyData.last_name || ""}
                    onChange={(e) => updateField("last_name", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Business Name</Label>
                  <Input
                    value={journeyData.business_name || ""}
                    onChange={(e) => updateField("business_name", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Location</Label>
                  <Input
                    value={journeyData.location || ""}
                    onChange={(e) => updateField("location", e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Social Media */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-600" />
                Social Media & Discovery
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Instagram</Label>
                  <Input
                    value={journeyData.discovery_channels?.instagram || ""}
                    onChange={(e) => updateDiscoveryChannel("instagram", e.target.value)}
                    placeholder="@handle"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">TikTok</Label>
                  <Input
                    value={journeyData.discovery_channels?.tiktok || ""}
                    onChange={(e) => updateDiscoveryChannel("tiktok", e.target.value)}
                    placeholder="@handle"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Website</Label>
                  <Input
                    value={journeyData.discovery_channels?.website || ""}
                    onChange={(e) => updateDiscoveryChannel("website", e.target.value)}
                    placeholder="yourwebsite.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">StyleSeat / Booksy</Label>
                  <Input
                    value={journeyData.discovery_channels?.styleseat || journeyData.discovery_channels?.booksy || ""}
                    onChange={(e) => updateDiscoveryChannel("styleseat", e.target.value)}
                    placeholder="Profile link"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Booking & Payments */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                Booking & Payments
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Booking Platforms</Label>
                  <p className="text-sm mt-1">
                    {(journeyData.booking_platforms || []).length > 0 
                      ? journeyData.booking_platforms.join(", ") 
                      : "None selected"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Payment Methods</Label>
                  <p className="text-sm mt-1">
                    {(journeyData.payment_methods || []).length > 0 
                      ? journeyData.payment_methods.join(", ") 
                      : "None selected"}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Services */}
            {journeyData.service_types?.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  Services Offered
                </h4>
                <p className="text-sm">{journeyData.service_types.join(", ")}</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowSummaryDialog(false)}
              className="w-full sm:w-auto"
            >
              Go Back & Edit
            </Button>
            <Button
              onClick={() => {
                setShowSummaryDialog(false);
                handleNext();
              }}
              disabled={saving}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirm & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper component for review sections
function ReviewSection({ title, icon: Icon, onEdit, items }) {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-purple-600" />
          <h4 className="font-medium">{title}</h4>
        </div>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Edit2 className="w-3 h-3 mr-1" />
          Edit
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((item, idx) => (
          <div key={idx} className="text-sm">
            <span className="text-gray-500">{item.label}:</span>{" "}
            <span className="font-medium">{item.value || " - "}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper functions
function formatArrayOrString(value) {
  if (Array.isArray(value)) return value.join(", ");
  return value || " - ";
}

function formatDiscoveryChannels(channels) {
  if (!channels || typeof channels !== 'object') return " - ";
  return Object.entries(channels)
    .filter(([k, v]) => v && v.active)
    .map(([k]) => k)
    .join(", ") || " - ";
}
